import { useEffect, type ReactNode } from "react";
import { cn } from "../../lib/cn";

export function useTitle(title: string | null | undefined) {
  useEffect(() => {
    if (!title) return;
    const prev = document.title;
    document.title = `${title} · DBIndex`;
    return () => { document.title = prev; };
  }, [title]);
}

export function Wrap({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("mx-auto max-w-7xl px-4 sm:px-6", className)}>{children}</div>;
}

export function Hero({ eyebrow, title, lede, children, aside }: { eyebrow?: ReactNode; title: ReactNode; lede?: ReactNode; children?: ReactNode; aside?: ReactNode }) {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="grid-bg pointer-events-none absolute inset-0 opacity-[0.3] [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" aria-hidden />
      <Wrap className={cn("relative py-14 lg:py-20", aside && "grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]")}>
        <div>
          {eyebrow && <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">{eyebrow}</p>}
          <h1 className="mt-3 max-w-3xl text-3xl font-semibold leading-[1.1] tracking-tight text-fg sm:text-5xl">{title}</h1>
          {lede && <p className="mt-5 max-w-2xl text-base leading-relaxed text-fg-2 sm:text-lg">{lede}</p>}
          {children && <div className="mt-7">{children}</div>}
        </div>
        {aside && <div className="min-w-0">{aside}</div>}
      </Wrap>
    </section>
  );
}

export function Section({ id, eyebrow, title, lede, children, className }: { id?: string; eyebrow?: string; title?: ReactNode; lede?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section id={id} className={cn("border-b border-border py-14", className)}>
      <Wrap>
        {eyebrow && <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">{eyebrow}</p>}
        {title && <h2 className="mt-2 max-w-3xl text-2xl font-semibold tracking-tight text-fg sm:text-3xl">{title}</h2>}
        {lede && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-fg-2 sm:text-base">{lede}</p>}
        <div className={cn((title || lede) && "mt-8")}>{children}</div>
      </Wrap>
    </section>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-panel px-4 py-3">
      <div className="text-xs text-fg-3">{label}</div>
      <div className="tabular mt-1 text-xl font-semibold text-fg">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-fg-3">{sub}</div>}
    </div>
  );
}

export function Pill({ children }: { children: ReactNode }) {
  return <span className="inline-flex items-center rounded border border-border bg-panel-2 px-1.5 py-0.5 font-mono text-[11px] text-fg-2">{children}</span>;
}

const REASONS: Record<string, string> = {
  price_exceeds_max: "Price is above your maximum",
  freshness_not_met: "Not fresh enough for your requirement",
  freshness_unknown: "Freshness not yet measured",
  quality_below_min: "Quality below your minimum",
  latency_exceeds_max: "Too slow for your latency limit",
  not_synced: "Not synced yet",
  dataset_not_in_constraints: "Excluded by your constraints",
  subject_not_covered: "Different subject",
  geo_not_covered: "Does not cover the requested place",
};
export const reasonText = (r: string) => REASONS[r] ?? r.replace(/_/g, " ");

export const pct = (x: number | null | undefined) => (x === null || x === undefined ? "—" : `${Math.round(x * 100)}%`);
